/**
 * World 1 — "Ion Coast" (110 BPM, cyan/magenta). Tutorial + 4 levels.
 * Design grammar: obstacles land on downbeats (every 4 rows = 2 beats);
 * 1-row gaps are the bread-and-butter jump; boost pads differentiate medals.
 */
import { buildLevel, every, rep, F, GAP } from "./build";
import type { LevelFile } from "../track/types";

/** ~22 s. One bit, no medals; three prompts ≤ 5 words (Appendix C). */
export const TUTORIAL: LevelFile = buildLevel({
  id: "tutorial",
  name: "First Flight",
  songId: "ion-coast",
  world: 1,
  noMedals: true,
  prompts: [
    { row: 4, text: "Steer ◄ ►" },
    { row: 32, text: "Jump on the beat ▲" },
    { row: 50, text: "Stay low in tunnels" },
  ],
  rows: [
    ...rep(F, 16),
    ...every(4, "__1____", "____1__", "_1_____", "_____1_"),
    ...rep(F, 8),
    GAP,
    ...rep(F, 3),
    GAP,
    ...rep(F, 3),
    ...rep(F, 8),
    ...rep("..TTT..", 6),
    ...rep(F, 6),
    "___o___",
    ...rep(F, 5),
    ...rep(F, 8),
    "FFFFFFF",
  ],
});

/** ~61 s. Gentle: singles, 1-row gaps, boost intro. 2 pads, 1 checkpoint. */
export const W1_L1: LevelFile = buildLevel({
  id: "w1-l1",
  name: "First Light",
  songId: "ion-coast",
  world: 1,
  rows: [
    ...rep(F, 16),
    ...every(4, "___1___", "__1____", "____1__", "_1___1_"),
    ...rep(F, 8),
    GAP,
    ...rep(F, 3),
    GAP,
    ...rep(F, 3),
    "_o_____",
    ...rep(F, 7),
    "___B___",
    ...rep(F, 11),
    // SkyRoads funnel: the road narrows to a single center ribbon and back
    ...rep("._____.", 3),
    ...rep("..___..", 3),
    ...rep("..._...", 4),
    ...rep("..___..", 2),
    ...rep("._____.", 2),
    ...rep(F, 2),
    ...rep(F, 4),
    GAP,
    ...rep(F, 3),
    "__1_1__",
    ...rep(F, 3),
    ...rep(F, 8),
    "CCCCCCC",
    ...rep(F, 7),
    ...every(4, "XX___XX", "_X___X_", "X__X__X"),
    "_____o_",
    ...rep(F, 7),
    "___B___",
    ...rep(F, 11),
    ...rep("..TTT..", 6),
    ...rep(F, 6),
    ...every(4, "__2____", "____2__", "_2___2_"),
    GAP,
    ...rep(F, 5),
    "___o___",
    ...rep(F, 7),
    ...rep(F, 8),
    ...every(4, "11___11", "111_111"),
    ...rep(F, 12),
    GAP,
    ...rep(F, 3),
    ...rep(F, 9),
    "FFFFFFF",
  ],
});

/** ~63 s. Tunnels + hazards. 2 pads, 1 checkpoint. */
export const W1_L2: LevelFile = buildLevel({
  id: "w1-l2",
  name: "Pierside",
  songId: "ion-coast",
  world: 1,
  rows: [
    ...rep(F, 12),
    ...every(4, "____1__", "__1____", "_1___1_", "___2___"),
    ...rep(F, 4),
    ...rep("..TTT..", 8),
    ...rep(F, 4),
    "o______",
    ...rep(F, 7),
    GAP,
    ...rep(F, 3),
    GAP,
    ...rep(F, 3),
    "___B___",
    ...rep(F, 11),
    ...every(4, "XXX_XXX", "XX___XX", "X__X__X", "_X_X_X_"),
    ...rep(F, 8),
    "CCCCCCC",
    ...rep(F, 7),
    ...rep(".TTTTT.", 6),
    "___o___",
    ...rep(F, 5),
    GAP,
    ...rep(F, 3),
    ...every(4, "_2___2_", "__2_2__", "_2___2_"),
    "___B___",
    ...rep(F, 11),
    // ribbon shift: left ribbon → wide overlap → right ribbon (pure steering)
    "_____..",
    ...rep("___....", 3),
    ...rep("______.", 2),
    ...rep("....___", 3),
    ...rep(F, 4),
    "X_____X",
    ...rep(F, 3),
    "XX___XX",
    ...rep(F, 3),
    ...rep("..TTT..", 6),
    ...rep(F, 4),
    "______o",
    ...rep(F, 7),
    GAP,
    ...rep(F, 3),
    GAP,
    ...rep(F, 3),
    ...every(4, "111_111", "11___11", "1__1__1"),
    ...rep(F, 12),
    GAP,
    ...rep(F, 5),
    ...rep(F, 11),
    "FFFFFFF",
  ],
});

/** ~65 s. Sticky pads + narrow roads. 3 pads, 2 checkpoints. */
export const W1_L3: LevelFile = buildLevel({
  id: "w1-l3",
  name: "Undertow",
  songId: "ion-coast",
  world: 1,
  rows: [
    ...rep(F, 12),
    ...every(4, "__1_1__", "_1___1_", "1__1__1", "_11_11_"),
    ...rep(F, 4),
    "___S___",
    ...rep(F, 7),
    ...every(4, "__1____", "____1__"),
    "_o_____",
    ...rep(F, 7),
    "___B___",
    ...rep(F, 11),
    GAP,
    ...rep(F, 3),
    GAP,
    ...rep(F, 3),
    ...rep("..___..", 8),
    "CCCCCCC",
    ...rep(F, 7),
    ...every(4, "XX_X_XX", "X_____X", "XXX_XXX"),
    "___o___",
    ...rep(F, 5),
    "__S_S__",
    ...rep(F, 7),
    ...every(4, "_2___2_", "__2_2__"),
    "___B___",
    ...rep(F, 11),
    ...rep("..TTT..", 8),
    ...rep(F, 4),
    "CCCCCCC",
    ...rep(F, 7),
    GAP,
    ...rep(F, 3),
    "_1___1_",
    ...rep(F, 3),
    // single-lane bridge with a gap IN it — jump without drifting an inch
    ...rep("..._...", 3),
    GAP,
    ...rep("..._...", 3),
    ...rep(F, 4),
    "______o",
    ...rep(F, 7),
    "___B___",
    ...rep(F, 11),
    ...every(4, "11_1_11", "111_111", "11___11"),
    GAP,
    ...rep(F, 3),
    ...rep(F, 10),
    GAP,
    ...rep(F, 3),
    ...rep(F, 15),
    "FFFFFFF",
  ],
});

/** ~70 s. The W1 finale: everything + boosted double-gap climax. 4 pads, 2 checkpoints. */
export const W1_L4: LevelFile = buildLevel({
  id: "w1-l4",
  name: "Ion Storm",
  songId: "ion-coast",
  world: 1,
  rows: [
    ...rep(F, 12),
    ...every(4, "_1_2_1_", "__2_2__", "_2_1_2_", "1__2__1"),
    GAP,
    ...rep(F, 3),
    GAP,
    ...rep(F, 3),
    "_o_____",
    ...rep(F, 7),
    "___B___",
    ...rep(F, 11),
    ...every(4, "XXX_XXX", "XX___XX", "X_XXX_X", "_XX_XX_"),
    ...rep("..TTT..", 8),
    ...rep(F, 4),
    "CCCCCCC",
    ...rep(F, 7),
    "__S_S__",
    ...rep(F, 7),
    ...every(4, "_2___2_", "2__1__2", "__2_2__"),
    GAP,
    ...rep(F, 3),
    "___o___",
    ...rep(F, 5),
    "___B___",
    ...rep(F, 11),
    // the campaign's first ISLAND HOPS: the ribbon ends, an offset lane
    // continues — jump sideways (Δ2 lanes, no forward void)
    ...rep("..___..", 2),
    "..__...",
    ...rep(".._....", 3),
    ...rep("...._..", 4),
    ...rep(".._....", 3),
    ...rep(F, 4),
    GAP,
    ...rep(F, 3),
    GAP,
    ...rep(F, 3),
    ...every(4, "111_111", "11_1_11", "1_111_1"),
    "CCCCCCC",
    ...rep(F, 7),
    "___B___",
    ...rep(F, 11),
    "XXXXXXX",
    ...rep(F, 3),
    "XXXXXXX",
    ...rep(F, 3),
    "______o",
    ...rep(F, 7),
    ...every(4, "__3_3__", "_3___3_"),
    "___B___",
    ...rep(F, 5),
    ...rep(F, 2),
    GAP,
    GAP, // boosted double gap (pad 8 rows back — jump rides the +25%)
    ...rep(F, 6),
    GAP,
    GAP, // unboosted double gap — the true expert edge jump
    ...rep(F, 10),
    ...rep(F, 15),
    "FFFFFFF",
  ],
});
